import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import localeNl from '@angular/common/locales/nl';
import {
  type ApplicationConfig,
  EnvironmentProviders,
  importProvidersFrom,
  inject,
  LOCALE_ID,
  Provider,
  provideAppInitializer,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideTransloco } from '@jsverse/transloco';
import { SharedConfigModule, translocoConfig } from '@maia/config';
import {
  AppApiService,
  AuthenticationInterceptor,
  AuthErrorInterceptor,
  mockEnvironmentProvider,
  MockAuthHeaderInterceptor,
  MockChatApiInterceptor,
  MockOidcSecurityService,
  ThemeService,
} from '@maia/core';
import { ApiService, provideAppConfig } from '@move-frontend/portal-toolkit';
import { OidcSecurityService, provideAuth, withAppInitializerAuthCheck } from 'angular-auth-oidc-client';
import dompurify from 'dompurify';
import { provideMarkdown, SANITIZE } from 'ngx-markdown';
import { environment } from '../environments/environment';
import { buildAuthConfigs } from './app.auth.config';
import { appRoutes } from './app.routes';

registerLocaleData(localeNl, 'nl-NL');

// sanitize function using an external library
function sanitizeHtml(html: string): string {
  return dompurify.sanitize(html, { ALLOW_DATA_ATTR: true });
}

type ProviderEntry = Provider | EnvironmentProviders;

const realAuthProviders: ProviderEntry[] = [
  provideAuth({ config: buildAuthConfigs(environment) }, withAppInitializerAuthCheck()),
  { provide: HTTP_INTERCEPTORS, useClass: AuthenticationInterceptor, multi: true },
  { provide: HTTP_INTERCEPTORS, useClass: AuthErrorInterceptor, multi: true },
];

const mockAuthProviders: ProviderEntry[] = [
  mockEnvironmentProvider(environment),
  { provide: OidcSecurityService, useExisting: MockOidcSecurityService },
  { provide: HTTP_INTERCEPTORS, useClass: MockAuthHeaderInterceptor, multi: true },
  { provide: HTTP_INTERCEPTORS, useClass: AuthErrorInterceptor, multi: true },
  { provide: HTTP_INTERCEPTORS, useClass: MockChatApiInterceptor, multi: true },
];

const authProviders: ProviderEntry[] = environment.useMocks ? mockAuthProviders : realAuthProviders;

export const appConfig: ApplicationConfig = {
  providers: [
    provideTransloco(translocoConfig),
    provideHttpClient(withInterceptorsFromDi()),
    provideAppInitializer((): void => {
      inject(ThemeService);
    }),
    importProvidersFrom(SharedConfigModule.register({ environment: environment })),
    ...authProviders,
    { provide: LOCALE_ID, useValue: 'nl-NL' },
    { provide: ApiService, useExisting: AppApiService },
    // env.apiUrl is unused: AppApiService overrides it per-app via AppContextService.
    provideAppConfig({ config: {}, env: { ...environment, apiUrl: '' } }),
    provideRouter(appRoutes),
    provideMarkdown({ sanitize: { provide: SANITIZE, useValue: sanitizeHtml } }),
  ],
};
